import { useEffect, useRef, useCallback } from "react";
import sha256 from "crypto-js/sha256";
import LogService from "../../service/log/LogService";

const service = new LogService('logSnapshot');

// Ensure we have a stable session id for the lifetime of the browser tab/session
function getOrCreateSessionId() {
    try {
        const key = "log_session_id";
        const existing = sessionStorage.getItem(key);
        if (existing) return existing;
        const sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        sessionStorage.setItem(key, sid);
        return sid;
    } catch (_) {
        // Fallback if sessionStorage not available
        return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }
}

// Helpers for persistent per-session dedup by assetId + hash
function loadSentHashes(sessionId, assetId) {
    try {
        const key = `snapshot_sent_hashes_${sessionId}_${assetId}`;
        const raw = sessionStorage.getItem(key);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return new Set(Array.isArray(arr) ? arr : []);
    } catch (_) {
        return new Set();
    }
}
function saveSentHashes(sessionId, assetId, set) {
    try {
        const key = `snapshot_sent_hashes_${sessionId}_${assetId}`;
        sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
    } catch (_) {
        // ignore
    }
}
function getInitialSent(sessionId, assetId) {
    try {
        const key = `snapshot_initial_sent_${sessionId}_${assetId}`;
        return sessionStorage.getItem(key) === "1";
    } catch (_) {
        return false;
    }
}
function setInitialSent(sessionId, assetId) {
    try {
        const key = `snapshot_initial_sent_${sessionId}_${assetId}`;
        sessionStorage.setItem(key, "1");
    } catch (_) {
        // ignore
    }
}

const DEFAULT_INLINE_PROPS = [
    // layout & box
    "display","position","top","right","bottom","left","z-index","box-sizing",
    "width","height","min-width","min-height","max-width","max-height",
    "margin","margin-top","margin-right","margin-bottom","margin-left",
    "padding","padding-top","padding-right","padding-bottom","padding-left",
    // typography & color
    "font","font-family","font-size","font-weight","line-height","letter-spacing",
    "color","text-align","text-decoration","text-transform","white-space",
    // backgrounds & borders
    "background","background-color","background-image","background-size","background-position","background-repeat",
    "border","border-radius","border-top","border-right","border-bottom","border-left",
    "box-shadow","opacity",
    // transforms & effects
    "transform","transform-origin","filter","mix-blend-mode",
    // overflow & visibility
    "overflow","overflow-x","overflow-y","visibility",
    // flex/grid
    "flex","flex-direction","flex-wrap","flex-grow","flex-shrink","flex-basis","justify-content","align-items","align-content",
    "grid","grid-template-columns","grid-template-rows","grid-auto-flow","grid-gap",
];

/**
 * useSnapshotLogger
 *
 * Amacı:
 * - Sayfanın/oynatıcı alanının tam bir HTML anlık görüntüsünü (snapshot) üretip loglamak.
 * - <style> ve <link rel="stylesheet"> öğelerini toplayıp, seçili CSS özelliklerini (computed styles) içerik
 *   elementlerine inline ederek tek dosyalık, taşınabilir bir HTML üretir.
 * - Kritik medya öğelerini (video, iframe, react-player) gerçek içerik yerine boyut-koruyan placeholder ile değiştirir;
 *   uygun olduğunda sınırlı sayıda video frame'i data URL olarak yakalamayı dener (aynı kaynak politikası izin verirse).
 * - Performansı korumak için hash tabanlı yinelenenleri engeller (dedup), olay bazlı tetiklemeleri throttle eder ve
 *   ilk snapshot'ı oturum (tab) başına yalnızca bir kez "snapshotInitial" olarak gönderir.
 *
 * Parametreler:
 * - assetId: string | number — Log kaydını ilişkilendirmek için içerik/oturum kimliği.
 * - options?: {
 *     enabled?: boolean = true,
 *     observeTargetSelector?: string = ".cvp-container-wrapper",   // Snapshot alınacak kök (bulunamazsa document.body)
 *     throttleMs?: number = 1000,                                   // Olay kaynaklı (Mutated/scroll/resize) tetiklemeler için throttle
 *     interval?: number = 30000,                                    // Periyodik çekim aralığı (ms)
 *     enablePeriodicCapture?: boolean = false,                      // Periyodik çekim aktif mi?
 *     scrollThrottle?: number = 2000,                               // (Ayrılmış) dış throttle; scheduleCapture içinde korunur
 *     enableScrollCapture?: boolean = true,                          // Scroll değişiminde snapshot al
 *     deferWhileVideoPlaying?: boolean = false,                     // Sadece periyodik tetiklemeleri oynatma sırasında ertele
 *     maxNodes?: number = 1500,                                     // Çok büyük DOM'larda style inline'ı atlar (fallback)
 *     inlineProps?: string[] = DEFAULT_INLINE_PROPS,                // Inline edilecek CSS özellikleri
 *     skipSelectors?: string = "video, audio, .video-gallery, .react-player, [data-skip-snapshot]",
 *     // removeSkippedSubtrees = true, // placeholder - currently not used
 *     skipInitialWhenMedia?: boolean = false,                       // Medya varsa ilk snapshot'ı atla
 *     playbackActiveOverride?: (() => boolean) | boolean | null = null, // Oynatma durumu override
 *     debug?: boolean = false,
 *     // NEW OPTIONS
 *     normalizeEphemeral?: boolean = true,                          // Ephemeral alanları normalleştir
 *     ephemeralSelector?: string = '[data-ephemeral], [data-scroll-progress], .scroll-progress, .scroll-to-top', // Normalleştirme için seçici
 *   }
 *
 * Üretilen Event Türleri (eventType):
 * - 'snapshotInitial'  — Bu oturumda (tab) ilgili asset için gönderilen ilk snapshot
 * - 'snapshotMutated'  — Kök DOM'da MutationObserver değişimlerinden sonra
 * - 'snapshotScroll'   — Scroll değişimi sonrası (passive dinleyici ile)
 * - 'snapshotResize'   — Pencere boyutu değiştiğinde
 * - 'snapshotPeriodic' — Periyodik zamanlayıcı ile (oynatma aktifse ve deferWhileVideoPlaying=true ise ertelenir)
 *
 * Gönderilen Metadata Şeması:
 * - hash: string                — HTML içeriğinin SHA-256 özeti; dedup için kullanılır
 * - url: string                 — window.location.href
 * - title: string               — document.title
 * - viewport: { w: number, h: number } — innerWidth/innerHeight
 * - scroll: { x: number, y: number }   — window.scrollX/scrollY
 * - html: string                — Inline stiller dâhil tek dosyalık HTML (büyük olabilir)
 * - capturedAt: string          — ISO timestamp
 *
 * Oturum Bazlı Davranışlar:
 * - sessionId: aynı tarayıcı sekmesi/oturumu boyunca sabit bir kimlik üretir ve her payload'a eklenir.
 * - Her assetId için, aynı oturumda gönderilmiş hash'ler sessionStorage'ta saklanır; aynı snapshot tekrar gönderilmez.
 * - Bir önceki gönderimle aynı hash ise (ani patlamaları azaltmak için) atlanır.
 *
 * Diğer Davranışlar ve Notlar:
 * - document.hidden iken snapshot alınmaz (görünürlük yoksa atla).
 * - Çok büyük DOM'larda (maxNodes üzerinde) yalnızca outerHTML döndürülür; style inline atlanır.
 * - Medya placeholder'ları, layout kaymasını önlemek için orijinal boyutları korur; sınırlı sayıda video frame dataURL
 *   yakalanabilir (aynı kaynak politikasına bağlıdır).
 * - SSR için tasarlanmamıştır; tarayıcı ortamında kullanılmalıdır.
 * - Geri dönüş değeri yoktur; hook yan etkiler üzerinden çalışır.
 *
 * Kısa Kullanım Örneği:
 *
 * function PlayerPage({ assetId }) {
 *   useSnapshotLogger(assetId, {
 *     enabled: true,
 *     enablePeriodicCapture: true,
 *     interval: 30000,
 *     deferWhileVideoPlaying: true,
 *     debug: false,
 *   });
 *   return <Player />;
 * }
 */
export const useSnapshotLogger = (
    assetId,
    options = {}
) => {
    const {
        enabled = true,
        observeTargetSelector = ".cvp-container-wrapper",
        throttleMs = 1000,
        interval = 30000,
        enablePeriodicCapture = false,
        scrollThrottle = 2000,
        enableScrollCapture = true,
        deferWhileVideoPlaying = false,
        maxNodes = 1500,
        inlineProps = DEFAULT_INLINE_PROPS,
        skipSelectors = "video, audio, .video-gallery, .react-player, [data-skip-snapshot]",
        skipInitialWhenMedia = false,
        playbackActiveOverride = null,
        debug = false,
        // NEW OPTIONS
        normalizeEphemeral = true,
        ephemeralSelector = '[data-ephemeral], [data-scroll-progress], .scroll-progress, .scroll-to-top',
    } = options;

    const sessionIdRef = useRef(getOrCreateSessionId());
    const lastHashRef = useRef(null);
    const throttleRef = useRef(null);
    const intervalRef = useRef(null);
    const observerRef = useRef(null);
    const playbackActiveRef = useRef(false);
    const sentHashesRef = useRef(new Set());
    const scrollThrottleRef = useRef(null);
    const resizeThrottleRef = useRef(null);
    const mutationThrottleRef = useRef(null);
    const initialCapturedRef = useRef(false);
    const initialSentRef = useRef(false); // NEW: Track if initial snapshot was sent
    const currentAssetIdRef = useRef(null);

    // NEW: Store callback references to avoid useEffect re-runs
    const buildSnapshotHtmlRef = useRef(null);
    const postSnapshotRef = useRef(null);
    const isPlaybackActiveRef = useRef(null);

    // Load persisted hash set whenever assetId or session changes
    useEffect(() => {
        if (!assetId) return;

        // Reset initial captured flag when asset changes
        if (currentAssetIdRef.current !== assetId) {
            currentAssetIdRef.current = assetId;
            initialCapturedRef.current = false;
            initialSentRef.current = false; // NEW: Reset initial sent flag
            lastHashRef.current = null;
            if (debug) console.log("[snapshot] asset changed, resetting flags", { newAssetId: assetId });
        }

        // Load initial sent status from sessionStorage
        const wasSent = getInitialSent(sessionIdRef.current, assetId);
        initialSentRef.current = wasSent;

        sentHashesRef.current = loadSentHashes(sessionIdRef.current, assetId);
        if (debug) console.log("[snapshot] loaded sent hash set", { size: sentHashesRef.current.size, assetId, initialSent: wasSent });
    }, [assetId, debug]);

    const computePlaybackActive = useCallback(() => {
        try {
            // HTMLMediaElements
            const media = Array.from(document.querySelectorAll("video, audio"));
            const anyMediaPlaying = media.some(m => !m.paused && !m.ended && m.readyState > 2);

            // Media Session API
            const ms = navigator.mediaSession;
            const msPlaying = !!ms && (ms.playbackState === "playing");

            // Heuristic for iframe-based providers (YouTube/Vimeo)
            const iframePlaying = Array.from(document.querySelectorAll('iframe[src*="youtube"], iframe[src*="vimeo"], iframe[src*="dailymotion"], iframe[src*="wistia"], iframe[src*="player"], .react-player')).length > 0 && (msPlaying || anyMediaPlaying);

            return anyMediaPlaying || msPlaying || iframePlaying;
        } catch (_) {
            return false;
        }
    }, []);

    useEffect(() => {
        // Track playback state proactively for native media
        const onPlay = () => { playbackActiveRef.current = true; };
        const onPlaying = () => { playbackActiveRef.current = true; };
        const onPause = () => { playbackActiveRef.current = computePlaybackActive(); };
        const onEnded = () => { playbackActiveRef.current = computePlaybackActive(); };

        document.addEventListener("play", onPlay, true);
        document.addEventListener("playing", onPlaying, true);
        document.addEventListener("pause", onPause, true);
        document.addEventListener("ended", onEnded, true);

        // Periodically reconcile with mediaSession if available
        const reconcile = setInterval(() => {
            playbackActiveRef.current = computePlaybackActive();
        }, 2000);

        return () => {
            document.removeEventListener("play", onPlay, true);
            document.removeEventListener("playing", onPlaying, true);
            document.removeEventListener("pause", onPause, true);
            document.removeEventListener("ended", onEnded, true);
            clearInterval(reconcile);
        };
    }, [computePlaybackActive]);

    // Capture current frame from a same-origin HTMLVideoElement; return dataURL or null on failure
    function tryCaptureVideoFrame(video, quality = 0.6) {
        try {
            const vw = Math.max(1, video.videoWidth || 0);
            const vh = Math.max(1, video.videoHeight || 0);
            if (!vw || !vh) return null;
            const canvas = document.createElement('canvas');
            canvas.width = vw; canvas.height = vh;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, vw, vh);
            return canvas.toDataURL('image/jpeg', quality);
        } catch (_) {
            return null;
        }
    }

    const applyMediaPlaceholders = useCallback((originalRoot, cloneRoot, { debug = false, captureVideoFrame = false, videoFrameQuality = 0.6, maxVideoFrames = 0 } = {}) => {
        try {
            // Videos -> fixed placeholder (no frame capture to keep hash stable)
            const origVideos = Array.from(originalRoot.querySelectorAll('video'));
            const cloneVideos = Array.from(cloneRoot.querySelectorAll('video'));
            const n = Math.min(origVideos.length, cloneVideos.length);
            for (let i = 0; i < n; i++) {
                const ov = origVideos[i];
                const cv = cloneVideos[i];
                const rect = ov.getBoundingClientRect();
                const w = Math.max(1, Math.floor(rect.width));
                const h = Math.max(1, Math.floor(rect.height));

                // Always use fixed placeholder div to ensure consistent hash
                const placeholder = document.createElement('div');
                placeholder.setAttribute('data-video-placeholder', 'true');
                placeholder.setAttribute('style', `display:block;width:${w}px;height:${h}px;background:#000;`);
                cv.replaceWith(placeholder);
            }

            // Iframes -> sized <div>
            const origIframes = Array.from(originalRoot.querySelectorAll('iframe'));
            const cloneIframes = Array.from(cloneRoot.querySelectorAll('iframe'));
            const m = Math.min(origIframes.length, cloneIframes.length);
            for (let i = 0; i < m; i++) {
                const of = origIframes[i];
                const cf = cloneIframes[i];
                const rect = of.getBoundingClientRect();
                const w = Math.max(1, Math.floor(rect.width));
                const h = Math.max(1, Math.floor(rect.height));
                const ph = document.createElement('div');
                ph.setAttribute('data-iframe-placeholder', 'true');
                ph.setAttribute('style', `display:block;width:${w}px;height:${h}px;background:#111;`);
                cf.replaceWith(ph);
            }

            // React Player containers -> sized <div>
            const origPlayers = Array.from(originalRoot.querySelectorAll('.react-player'));
            const clonePlayers = Array.from(cloneRoot.querySelectorAll('.react-player'));
            const k = Math.min(origPlayers.length, clonePlayers.length);
            for (let i = 0; i < k; i++) {
                const op = origPlayers[i];
                const cp = clonePlayers[i];
                const rect = op.getBoundingClientRect();
                const w = Math.max(1, Math.floor(rect.width));
                const h = Math.max(1, Math.floor(rect.height));
                const ph = document.createElement('div');
                ph.setAttribute('data-player-placeholder', 'true');
                ph.setAttribute('style', `display:block;width:${w}px;height:${h}px;background:#111;`);
                cp.replaceWith(ph);
            }
        } catch (e) {
            if (debug) console.warn('[snapshot] applyMediaPlaceholders failed', e);
        }
    }, [debug]);

    const inlineAllStyles = useCallback((element) => {
        const clone = element.cloneNode(true);

        // Media placeholders first to keep layout
        applyMediaPlaceholders(element, clone);

        // Establish skip roots within the cloned tree
        let skipRoots = [];
        try {
            skipRoots = Array.from(clone.querySelectorAll(skipSelectors));
            // Do NOT remove entirely; we already placed placeholders where needed
            // We will simply avoid heavy inlining on these nodes
        } catch (_) {
            skipRoots = [];
        }

        // Skip style inlining entirely if node count is too large
        const nodes = [clone, ...clone.querySelectorAll("*")];
        const tooManyNodes = nodes.length > maxNodes;

        if (tooManyNodes) {
            return clone.outerHTML; // fallback: no inline on very large DOMs
        }

        nodes.forEach((el) => {
            try {
                // Fast skip for any element inside a skip root
                if (skipRoots.length && skipRoots.some(root => root === el || root.contains(el))) return;

                const computed = getComputedStyle(el);
                let styleString = "";
                for (let i = 0; i < inlineProps.length; i++) {
                    const prop = inlineProps[i];
                    const val = computed.getPropertyValue(prop);
                    if (val) styleString += `${prop}:${val};`;
                }
                const existing = el.getAttribute("style");
                el.setAttribute("style", existing ? `${existing};${styleString}` : styleString);
            } catch (_) {
                // ignore elements that may throw
            }
        });

        return clone.outerHTML;
    }, [applyMediaPlaceholders, inlineProps, maxNodes, skipSelectors, debug]);

    const collectStyleTags = useCallback(() => {
        try {
            return Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
                .map((node) => {
                    if (node.tagName === "STYLE") {
                        return `<style>${node.innerHTML}</style>`;
                    } else if (node.tagName === "LINK") {
                        const href = node.getAttribute("href") || node.href || "";
                        const media = node.getAttribute("media");
                        return `<link rel="stylesheet" href="${href}"${media ? ` media="${media}"` : ""}>`;
                    }
                    return "";
                })
                .join("\n");
        } catch (_) {
            return "";
        }
    }, []);

    const buildSnapshotHtml = useCallback(() => {
        const target = document.querySelector(observeTargetSelector) || document.body;
        if (!target) {
            if (debug) console.warn("[snapshot] target not found", { observeTargetSelector });
            return null;
        }

        if (document.hidden) {
            if (debug) console.log("[snapshot] skipped: document.hidden");
            return null;
        }

        const styles = collectStyleTags();
        const content = inlineAllStyles(target);
        const lang = document.documentElement?.lang || (navigator.language ? navigator.language.split('-')[0] : 'en');
        const title = document.title || '';

        // Build HTML without timestamps or dynamic data that changes the hash
        const html = `<!DOCTYPE html>\n<html lang="${lang}">\n<head>\n<meta charset="utf-8">\n<title>${title}</title>\n${styles}\n</head>\n<body>\n${content}\n</body>\n</html>`;
        if (debug) console.log("[snapshot] built", { length: html.length });
        return html;
    }, [collectStyleTags, inlineAllStyles, observeTargetSelector, debug]);

    const lastNormalizedForHashRef = useRef(null);

    const normalizeHtmlForHash = useCallback((html, eventType) => {
        if (!normalizeEphemeral) return html;
        // Şimdilik sadece scroll event'lerinde normalizasyon uyguluyoruz
        if (eventType !== 'scroll' && eventType !== 'mutated') return html; // mutated da çok sık tetiklenen ilerleme göstergelerini stabilize etsin diye dahil
        try {
            // Büyük HTML parse maliyetini azaltmak için hızlı kısa devre: selector string'i yoksa parse etme
            if (!ephemeralSelector || !ephemeralSelector.trim()) return html;
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const nodes = doc.querySelectorAll(ephemeralSelector);
            if (!nodes.length) return html;
            nodes.forEach(n => {
                try {
                    // İçerik sadece hash'e etki etmeyen sabit bir placeholder'a döner
                    // Yapıyı korumak için çocukları temizleyip tek bir text node koyuyoruz
                    while (n.firstChild) n.removeChild(n.firstChild);
                    const span = doc.createElement('span');
                    span.setAttribute('data-ephemeral-placeholder', '1');
                    span.textContent = 'EPHEMERAL';
                    n.appendChild(span);
                    n.setAttribute('data-ephemeral-normalized', '1');
                } catch (_) { /* ignore */ }
            });
            const normalized = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
            return normalized;
        } catch (e) {
            if (debug) console.warn('[snapshot] normalizeHtmlForHash failed', e);
            return html;
        }
    }, [normalizeEphemeral, ephemeralSelector, debug]);

    const postSnapshot = useCallback(
        (eventType, snapshotHtml) => {
            if (!snapshotHtml) return;

            // Normalize ephemeral dynamic regions BEFORE hashing to avoid false-positive diffs
            const normalizedForHash = normalizeHtmlForHash(snapshotHtml, eventType);

            // Hash ONLY the (possibly normalized) HTML content
            const hash = sha256(normalizedForHash).toString();

            if (debug) {
                const changedByNormalization = normalizedForHash.length !== snapshotHtml.length;
                console.log("[snapshot] computed hash", { eventType, hash, htmlLength: snapshotHtml.length, normalizedLength: normalizedForHash.length, normalized: changedByNormalization });
            }

            // Persistent per-session dedup (skip if we already sent this hash for this asset in this session)
            if (sentHashesRef.current.has(hash)) {
                if (debug) console.log("[snapshot] persistent dedup skipped - hash already sent", { eventType, hash });
                return;
            }

            // Also dedup the immediate previous hash to reduce bursts (based on normalized content)
            if (hash === lastHashRef.current) {
                if (debug) console.log("[snapshot] last-hash dedup skipped - same as previous", { eventType, hash });
                return;
            }

            // Capitalize first letter of eventType for camelCase
            const capitalizedEventType = eventType.charAt(0).toUpperCase() + eventType.slice(1);

            // If this is the first snapshot for this asset/session, force the event type to Initial
            const isFirstForSession = !getInitialSent(sessionIdRef.current, assetId) && !initialSentRef.current;
            const finalEventType = isFirstForSession ? "snapshotInitial" : `snapshot${capitalizedEventType}`;

            // Metadata includes timestamp and dynamic info, but these don't affect the hash
            const metadata = {
                hash,
                url: window.location.href,
                title: document.title,
                viewport: { w: window.innerWidth, h: window.innerHeight },
                scroll: { x: window.scrollX, y: window.scrollY },
                html: snapshotHtml, // Orijinal (normalize edilmemiş) HTML'i gönderiyoruz
                capturedAt: new Date().toISOString(),
            };

            if (debug) console.log("[snapshot] sending to server", { eventType: finalEventType, hash, size: snapshotHtml.length, isFirstForSession });

            service.create({
                eventType: finalEventType,
                assetId,
                sessionId: sessionIdRef.current,
                metadata: JSON.stringify(metadata),
            }).then((res) => {
                // On success: remember last hash and persist in session set
                lastHashRef.current = hash;
                sentHashesRef.current.add(hash);
                saveSentHashes(sessionIdRef.current, assetId, sentHashesRef.current);
                if (isFirstForSession) setInitialSent(sessionIdRef.current, assetId);
                initialSentRef.current = true; // Mark initial snapshot as sent
                if (debug) console.log("[snapshot] sent successfully", { eventType: finalEventType, hash, res });
            }).catch((err) => {
                if (debug) console.error("[snapshot] send failed", { eventType: finalEventType, hash, error: err });
            });
        },
        [assetId, debug, normalizeHtmlForHash]
    );

    const isPlaybackActive = useCallback(() => {
        if (typeof playbackActiveOverride === 'function') {
            try { return !!playbackActiveOverride(); } catch { /* ignore */ }
        } else if (typeof playbackActiveOverride === 'boolean') {
            return playbackActiveOverride;
        }
        return (playbackActiveRef.current || computePlaybackActive());
    }, [computePlaybackActive, playbackActiveOverride]);

    useEffect(() => {
        if (!enabled || !assetId) return;

        // Define scheduleCapture inside useEffect to avoid dependency issues
        const scheduleCapture = (reason) => {
            const normalizedReason = reason.toLowerCase();

            // Prevent ONLY initial capture from running multiple times
            if (normalizedReason === "initial") {
                if (initialCapturedRef.current) {
                    if (debug) console.log("[snapshot] initial already captured, skipping");
                    return;
                }
                // Mark that we're about to capture initial (set flag BEFORE scheduling)
                initialCapturedRef.current = true;
            }

            // Only gate periodic captures during playback
            if (normalizedReason === "periodic" && deferWhileVideoPlaying) {
                const isActive = typeof playbackActiveOverride === 'function'
                    ? playbackActiveOverride()
                    : (typeof playbackActiveOverride === 'boolean' ? playbackActiveOverride : (playbackActiveRef.current || computePlaybackActive()));

                if (isActive) {
                    if (debug) console.log("[snapshot] periodic gated (playing)");
                    return;
                }
            }

            const doCapture = () => {
                throttleRef.current = null;
                if (debug) console.log("[snapshot] capturing", { reason: normalizedReason });
                const html = buildSnapshotHtml();
                postSnapshot(normalizedReason, html);
            };

            if (throttleRef.current) {
                clearTimeout(throttleRef.current);
                throttleRef.current = null;
            }

            const delay = Math.max(250, normalizedReason === "initial" ? 0 : throttleMs);
            const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, delay));
            throttleRef.current = setTimeout(() => {
                if (debug) console.log("[snapshot] scheduling idle", { delay, reason: normalizedReason });
                ric(doCapture, { timeout: delay });
            }, delay);
        };

        if (debug) console.log("[snapshot] useEffect started", { assetId, enabled });

        // Initial capture - only once per asset
        const hasMedia = !!document.querySelector("video, audio, .react-player, [data-skip-snapshot]");
        if (!(skipInitialWhenMedia && hasMedia)) {
            if (debug) console.log("[snapshot] scheduling initial capture");
            scheduleCapture("initial");
        } else if (debug) {
            console.log("[snapshot] Initial skipped due to media");
        }

        // Mutation observer with intelligent filtering
        const targetNode = document.querySelector(observeTargetSelector) || document.body;
        if (targetNode && typeof MutationObserver !== "undefined") {
            let mutationCount = 0;
            const MUTATION_BATCH_THRESHOLD = 5; // Only capture after significant changes

            const observer = new MutationObserver((mutations) => {
                if (mutationThrottleRef.current) return;

                // Filter out trivial mutations
                const significantMutations = mutations.filter(mutation => {
                    // Ignore attribute changes that are typically animations/styling
                    if (mutation.type === 'attributes') {
                        const attrName = mutation.attributeName;
                        // Skip common animation/styling attributes
                        if (['class', 'style', 'aria-valuenow', 'aria-valuetext', 'data-progress'].includes(attrName)) {
                            // Only consider significant if the element has substantial content
                            const target = mutation.target;
                            if (target && target.nodeType === 1) {
                                const text = target.textContent || '';
                                // Skip if element has less than 50 chars or is likely a progress/counter
                                if (text.length < 50 || /^\d+(\.\d+)?%?$/.test(text.trim())) {
                                    return false;
                                }
                            }
                        }
                    }

                    // Ignore small text changes (likely counters or timers)
                    if (mutation.type === 'characterData') {
                        const oldValue = mutation.oldValue || '';
                        const newValue = mutation.target.textContent || '';
                        // Skip if both are short and numeric-like
                        if (oldValue.length < 20 && newValue.length < 20) {
                            if (/^[\d:.\s\-\/]+$/.test(oldValue) && /^[\d:.\s\-\/]+$/.test(newValue)) {
                                return false;
                            }
                        }
                    }

                    // Consider childList changes significant
                    if (mutation.type === 'childList') {
                        // But skip if only text nodes changed
                        const hasElementChanges = Array.from(mutation.addedNodes).some(n => n.nodeType === 1) ||
                                                 Array.from(mutation.removedNodes).some(n => n.nodeType === 1);
                        return hasElementChanges;
                    }

                    return true;
                });

                if (significantMutations.length === 0) return;

                // Batch mutations - only capture after multiple significant changes
                mutationCount += significantMutations.length;
                if (mutationCount < MUTATION_BATCH_THRESHOLD) {
                    // Reset counter after a delay
                    setTimeout(() => { mutationCount = 0; }, 5000);
                    return;
                }

                mutationCount = 0;
                mutationThrottleRef.current = setTimeout(() => {
                    mutationThrottleRef.current = null;
                    if (debug) console.log("[snapshot] significant mutations detected, scheduling capture", { count: significantMutations.length });
                    scheduleCapture("mutated");
                }, throttleMs * 2); // Double the throttle for mutations
            });
            observer.observe(targetNode, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeOldValue: false,
                characterData: true,
                characterDataOldValue: true,
            });
            observerRef.current = observer;
        }

        // Scroll/resize events with proper throttling
        const onScroll = () => {
            if (scrollThrottleRef.current) return;
            scrollThrottleRef.current = setTimeout(() => {
                scrollThrottleRef.current = null;
                if (debug) console.log("[snapshot] scroll detected, scheduling capture");
                scheduleCapture("scroll");
            }, scrollThrottle);
        };

        const onResize = () => {
            if (resizeThrottleRef.current) return;
            resizeThrottleRef.current = setTimeout(() => {
                resizeThrottleRef.current = null;
                if (debug) console.log("[snapshot] resize detected, scheduling capture");
                scheduleCapture("resize");
            }, throttleMs);
        };

        if (enableScrollCapture) {
            window.addEventListener("scroll", onScroll, { passive: true });
            if (debug) console.log("[snapshot] scroll listener attached");
        }
        window.addEventListener("resize", onResize, { passive: true });
        if (debug) console.log("[snapshot] resize listener attached");

        // Periodic interval
        if (enablePeriodicCapture && interval > 0) {
            intervalRef.current = setInterval(() => {
                if (debug) console.log("[snapshot] periodic timer fired, scheduling capture");
                scheduleCapture("periodic");
            }, interval);
            if (debug) console.log("[snapshot] periodic timer started", { interval });
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (throttleRef.current) {
                clearTimeout(throttleRef.current);
                throttleRef.current = null;
            }
            if (scrollThrottleRef.current) {
                clearTimeout(scrollThrottleRef.current);
                scrollThrottleRef.current = null;
            }
            if (resizeThrottleRef.current) {
                clearTimeout(resizeThrottleRef.current);
                resizeThrottleRef.current = null;
            }
            if (mutationThrottleRef.current) {
                clearTimeout(mutationThrottleRef.current);
                mutationThrottleRef.current = null;
            }

            if (enableScrollCapture) {
                window.removeEventListener("scroll", onScroll);
            }
            window.removeEventListener("resize", onResize);

            if (debug) console.log("[snapshot] cleanup done");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId, enabled, observeTargetSelector, enablePeriodicCapture, interval, enableScrollCapture, scrollThrottle, skipInitialWhenMedia, throttleMs, deferWhileVideoPlaying]);

    // Hook has no return value; it operates via side effects only
};
